/**
 * SettingsPage
 *
 * Tabbed email settings: General, Signatures, Filters, Templates, Snippets,
 * Modules, and Notifications.
 */

import { useState, useCallback } from 'react';
import {
  Settings,
  Pen,
  Filter,
  FileText,
  Zap,
  Puzzle,
  Bell,
  Plus,
  Trash2,
  Edit,
  TestTube,
  ToggleLeft,
  ToggleRight,
  UserCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { useEmailAccounts } from '../hooks/useEmailAccounts';
import { InboxPage } from './InboxPage';
import { useEmailSettings } from '../hooks/useEmailSettings';
import { useSignatures } from '../hooks/useSignatures';
import { useFilters } from '../hooks/useFilters';
import { useTemplates } from '../hooks/useTemplates';
import { useSnippets } from '../hooks/useSnippets';
import { useEmailModules } from '../hooks/useEmailModules';
import { api } from '@/lib/api';
import type { EmailFilterCondition, EmailFilterAction } from '@/lib/api';

// ============================================================================
// ACCOUNTS TAB
// ============================================================================

function AccountsTab() {
  const { accounts, loading, fetchAccounts } = useEmailAccounts();
  const { toast } = useToast();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');

  const handleCreate = useCallback(async () => {
    if (!newUsername.trim()) return;
    setCreating(true);
    try {
      await api.createEmailAccount({
        username: newUsername.trim(),
        displayName: newDisplayName.trim() || undefined,
      });
      await fetchAccounts();
      setShowAddDialog(false);
      setNewUsername('');
      setNewDisplayName('');
      toast({ title: 'Account created' });
    } catch (err) {
      toast({
        title: 'Failed to create account',
        description: err instanceof Error ? err.message : 'Try a different username',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }, [newUsername, newDisplayName, fetchAccounts, toast]);

  const handleUpdateDisplayName = useCallback(
    async (accountId: string) => {
      try {
        await api.updateEmailAccount(accountId, {
          displayName: editDisplayName.trim() || undefined,
        });
        await fetchAccounts();
        setEditingId(null);
        toast({ title: 'Display name updated' });
      } catch {
        toast({ title: 'Failed to update', variant: 'destructive' });
      }
    },
    [editDisplayName, fetchAccounts, toast],
  );

  const handleHideAccount = useCallback(
    async (accountId: string) => {
      try {
        await api.deleteEmailAccount(accountId);
        await fetchAccounts();
        toast({ title: 'Account hidden' });
      } catch {
        toast({ title: 'Failed to hide account', variant: 'destructive' });
      }
    },
    [fetchAccounts, toast],
  );

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4 max-w-xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Manage your email accounts. Each account gets its own @fias.email address.
        </p>
        <Button size="sm" onClick={() => setShowAddDialog(true)} className="gap-1.5 flex-shrink-0">
          <Plus className="h-4 w-4" /> Add Account
        </Button>
      </div>

      {accounts.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">
          No accounts yet. Add one to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map((acc) => (
            <Card key={acc.id}>
              <CardHeader className="py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      {(acc.displayName || acc.emailAddress).charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      {editingId === acc.id ? (
                        <div className="flex gap-1.5">
                          <Input
                            value={editDisplayName}
                            onChange={(e) => setEditDisplayName(e.target.value)}
                            placeholder="Display name"
                            className="h-7 text-sm w-40"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateDisplayName(acc.id);
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                          />
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => handleUpdateDisplayName(acc.id)}
                          >
                            Save
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => setEditingId(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <>
                          <CardTitle className="text-sm">
                            {acc.displayName || acc.emailAddress}
                          </CardTitle>
                          <CardDescription className="text-xs">{acc.emailAddress}</CardDescription>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {acc.isPrimary && (
                      <Badge variant="secondary" className="text-xs">
                        Primary
                      </Badge>
                    )}
                    <Badge
                      variant={acc.status === 'active' ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {acc.status === 'active' ? (
                        <>
                          <Eye className="h-3 w-3 mr-1" /> Active
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-3 w-3 mr-1" /> Hidden
                        </>
                      )}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-3 flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => {
                    setEditingId(acc.id);
                    setEditDisplayName(acc.displayName || '');
                  }}
                >
                  <Edit className="h-3 w-3" /> Edit Name
                </Button>
                {acc.status === 'active' && !acc.isPrimary && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs gap-1 text-destructive"
                    onClick={() => handleHideAccount(acc.id)}
                  >
                    <EyeOff className="h-3 w-3" /> Hide Account
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Account Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Email Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Username</Label>
              <div className="flex gap-2 items-center">
                <Input
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="username"
                  className="text-sm"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreate();
                  }}
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">@fias.email</span>
              </div>
            </div>
            <div>
              <Label className="text-sm">Display Name (optional)</Label>
              <Input
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Your Name"
                className="text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating || !newUsername.trim()}>
              {creating ? 'Creating...' : 'Create Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// GENERAL TAB
// ============================================================================

function GeneralTab({ accountId }: { accountId: string | undefined }) {
  const { settings, loading, updateSettings } = useEmailSettings(accountId);
  const { toast } = useToast();

  const save = useCallback(
    async (field: string, value: unknown) => {
      try {
        await updateSettings({ [field]: value });
        toast({ title: 'Settings saved' });
      } catch {
        toast({ title: 'Failed to save', variant: 'destructive' });
      }
    },
    [updateSettings, toast],
  );

  if (!accountId) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        Create an email account first to configure settings.
      </div>
    );
  }

  if (loading || !settings) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      {/* Reading pane */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Reading pane position</Label>
        <div className="flex gap-3">
          {(['right', 'bottom', 'off'] as const).map((v) => (
            <Button
              key={v}
              variant={settings.readingPane === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => save('readingPane', v)}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Messages per page */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Messages per page</Label>
        <Select
          value={String(settings.messagesPerPage)}
          onValueChange={(v) => save('messagesPerPage', parseInt(v))}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25</SelectItem>
            <SelectItem value="50">50</SelectItem>
            <SelectItem value="100">100</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Conversation view */}
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Conversation view</Label>
        <Switch
          checked={settings.conversationView}
          onCheckedChange={(v) => save('conversationView', v)}
        />
      </div>

      {/* Auto-advance */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">After archiving or deleting</Label>
        <div className="flex gap-3">
          {(['next', 'previous', 'list'] as const).map((v) => (
            <Button
              key={v}
              variant={settings.autoAdvance === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => save('autoAdvance', v)}
            >
              {v === 'list' ? 'Back to list' : `Go to ${v}`}
            </Button>
          ))}
        </div>
      </div>

      {/* Time format */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Time format</Label>
        <div className="flex gap-3">
          {(['12h', '24h'] as const).map((v) => (
            <Button
              key={v}
              variant={settings.timeFormat === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => save('timeFormat', v)}
            >
              {v}
            </Button>
          ))}
        </div>
      </div>

      {/* Undo send delay */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Undo send delay</Label>
        <Select
          value={String(settings.undoSendDelaySeconds)}
          onValueChange={(v) => save('undoSendDelaySeconds', parseInt(v))}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="5">5 seconds</SelectItem>
            <SelectItem value="10">10 seconds</SelectItem>
            <SelectItem value="20">20 seconds</SelectItem>
            <SelectItem value="30">30 seconds</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ============================================================================
// SIGNATURES TAB
// ============================================================================

function SignaturesTab({ accountId }: { accountId: string | undefined }) {
  const { signatures, loading, createSignature, updateSignature, deleteSignature } =
    useSignatures(accountId);
  const { toast } = useToast();
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editDefault, setEditDefault] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const openCreate = useCallback(() => {
    setEditId(null);
    setEditName('');
    setEditBody('');
    setEditDefault(false);
    setShowDialog(true);
  }, []);

  const openEdit = useCallback(
    (id: string) => {
      const sig = signatures.find((s) => s.id === id);
      if (!sig) return;
      setEditId(id);
      setEditName(sig.name);
      setEditBody(sig.bodyHtml);
      setEditDefault(sig.isDefault);
      setShowDialog(true);
    },
    [signatures],
  );

  const handleSave = useCallback(async () => {
    if (!accountId) return;
    try {
      if (editId) {
        await updateSignature(editId, {
          name: editName,
          bodyHtml: editBody,
          isDefault: editDefault,
        });
      } else {
        await createSignature({
          accountId,
          name: editName,
          bodyHtml: editBody,
          isDefault: editDefault,
        });
      }
      setShowDialog(false);
      toast({ title: editId ? 'Signature updated' : 'Signature created' });
    } catch {
      toast({ title: 'Failed to save signature', variant: 'destructive' });
    }
  }, [accountId, editId, editName, editBody, editDefault, createSignature, updateSignature, toast]);

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4 max-w-xl">
      <Button size="sm" onClick={openCreate} className="gap-1.5">
        <Plus className="h-4 w-4" /> Create signature
      </Button>

      {signatures.map((sig) => (
        <Card key={sig.id}>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm">{sig.name}</CardTitle>
                {sig.isDefault && (
                  <Badge variant="secondary" className="text-xs">
                    Default
                  </Badge>
                )}
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => openEdit(sig.id)}
                >
                  <Edit className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => deleteSignature(sig.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div
              className="text-xs text-muted-foreground line-clamp-2"
              dangerouslySetInnerHTML={{ __html: sig.bodyHtml }}
            />
          </CardContent>
        </Card>
      ))}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Signature' : 'Create Signature'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Signature name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <textarea
              className="w-full min-h-[120px] p-2 text-sm border border-border rounded-md bg-background"
              placeholder="Signature HTML..."
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Switch checked={editDefault} onCheckedChange={setEditDefault} />
              <Label className="text-sm">Set as default</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!editName.trim() || !editBody.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// FILTERS TAB
// ============================================================================

const CONDITION_FIELDS = ['from', 'to', 'subject', 'body', 'has_attachment'] as const;
const CONDITION_OPERATORS = [
  'contains',
  'not_contains',
  'equals',
  'starts_with',
  'ends_with',
  'matches_regex',
] as const;
const ACTION_TYPES = [
  'move_to_folder',
  'add_label',
  'mark_read',
  'mark_starred',
  'archive',
  'trash',
  'forward_to',
  'set_importance',
  'set_category',
] as const;

function FiltersTab({ accountId }: { accountId: string | undefined }) {
  const { filters, loading, createFilter, updateFilter, deleteFilter, testFilter } =
    useFilters(accountId);
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editConditions, setEditConditions] = useState<EmailFilterCondition[]>([
    { field: 'from', operator: 'contains', value: '' },
  ]);
  const [editActions, setEditActions] = useState<EmailFilterAction[]>([
    { type: 'mark_read', value: 'true' },
  ]);
  const [editEnabled, setEditEnabled] = useState(true);
  const [testResult, setTestResult] = useState<string | null>(null);

  const openCreate = useCallback(() => {
    setEditId(null);
    setEditName('');
    setEditConditions([{ field: 'from', operator: 'contains', value: '' }]);
    setEditActions([{ type: 'mark_read', value: 'true' }]);
    setEditEnabled(true);
    setTestResult(null);
    setShowDialog(true);
  }, []);

  const openEdit = useCallback(
    (id: string) => {
      const f = filters.find((x) => x.id === id);
      if (!f) return;
      setEditId(id);
      setEditName(f.name);
      setEditConditions(f.conditions as EmailFilterCondition[]);
      setEditActions(f.actions as EmailFilterAction[]);
      setEditEnabled(f.isEnabled);
      setTestResult(null);
      setShowDialog(true);
    },
    [filters],
  );

  const handleSave = useCallback(async () => {
    if (!accountId) return;
    try {
      if (editId) {
        await updateFilter(editId, {
          name: editName,
          conditions: editConditions,
          actions: editActions,
          isEnabled: editEnabled,
        });
      } else {
        await createFilter({
          accountId,
          name: editName,
          conditions: editConditions,
          actions: editActions,
          isEnabled: editEnabled,
        });
      }
      setShowDialog(false);
      toast({ title: editId ? 'Filter updated' : 'Filter created' });
    } catch {
      toast({ title: 'Failed to save filter', variant: 'destructive' });
    }
  }, [
    accountId,
    editId,
    editName,
    editConditions,
    editActions,
    editEnabled,
    createFilter,
    updateFilter,
    toast,
  ]);

  const handleTest = useCallback(async () => {
    if (!editId) return;
    const result = await testFilter(editId);
    setTestResult(`${result.matchingMessageIds.length} of ${result.totalTested} messages match`);
  }, [editId, testFilter]);

  const handleToggle = useCallback(
    async (filterId: string, enabled: boolean) => {
      await updateFilter(filterId, { isEnabled: enabled });
    },
    [updateFilter],
  );

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4 max-w-2xl">
      <Button size="sm" onClick={openCreate} className="gap-1.5">
        <Plus className="h-4 w-4" /> Create filter
      </Button>

      {filters.map((f) => (
        <Card key={f.id}>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-sm">{f.name}</CardTitle>
                <Badge variant={f.isEnabled ? 'default' : 'secondary'} className="text-xs">
                  {f.isEnabled ? 'Active' : 'Disabled'}
                </Badge>
              </div>
              <div className="flex gap-1">
                <Switch checked={f.isEnabled} onCheckedChange={(v) => handleToggle(f.id, v)} />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => openEdit(f.id)}
                >
                  <Edit className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => deleteFilter(f.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <p className="text-xs text-muted-foreground">
              {(f.conditions as EmailFilterCondition[])
                .map((c) => `${c.field} ${c.operator} "${c.value}"`)
                .join(' AND ')}
              {' → '}
              {(f.actions as EmailFilterAction[]).map((a) => a.type).join(', ')}
            </p>
          </CardContent>
        </Card>
      ))}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Filter' : 'Create Filter'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <Input
              placeholder="Filter name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />

            {/* Conditions */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Conditions (all must match)</Label>
              {editConditions.map((c, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select
                    value={c.field}
                    onValueChange={(v) => {
                      const updated = [...editConditions];
                      updated[i] = { ...c, field: v as EmailFilterCondition['field'] };
                      setEditConditions(updated);
                    }}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_FIELDS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={c.operator}
                    onValueChange={(v) => {
                      const updated = [...editConditions];
                      updated[i] = { ...c, operator: v as EmailFilterCondition['operator'] };
                      setEditConditions(updated);
                    }}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPERATORS.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o.replace('_', ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    className="flex-1"
                    value={c.value}
                    onChange={(e) => {
                      const updated = [...editConditions];
                      updated[i] = { ...c, value: e.target.value };
                      setEditConditions(updated);
                    }}
                    placeholder="Value"
                  />
                  {editConditions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setEditConditions(editConditions.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setEditConditions([
                    ...editConditions,
                    { field: 'from', operator: 'contains', value: '' },
                  ])
                }
              >
                <Plus className="h-3 w-3 mr-1" /> Add condition
              </Button>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Actions</Label>
              {editActions.map((a, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select
                    value={a.type}
                    onValueChange={(v) => {
                      const updated = [...editActions];
                      updated[i] = { ...a, type: v as EmailFilterAction['type'] };
                      setEditActions(updated);
                    }}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTION_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t.replace(/_/g, ' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    className="flex-1"
                    value={a.value}
                    onChange={(e) => {
                      const updated = [...editActions];
                      updated[i] = { ...a, value: e.target.value };
                      setEditActions(updated);
                    }}
                    placeholder="Value (folder ID, label ID, etc.)"
                  />
                  {editActions.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setEditActions(editActions.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setEditActions([...editActions, { type: 'mark_read', value: 'true' }])
                }
              >
                <Plus className="h-3 w-3 mr-1" /> Add action
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={editEnabled} onCheckedChange={setEditEnabled} />
              <Label className="text-sm">Enabled</Label>
            </div>

            {editId && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleTest} className="gap-1">
                  <TestTube className="h-3.5 w-3.5" /> Test filter
                </Button>
                {testResult && <span className="text-xs text-muted-foreground">{testResult}</span>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!editName.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// TEMPLATES TAB
// ============================================================================

function TemplatesTab({ accountId }: { accountId: string | undefined }) {
  const { templates, loading, createTemplate, updateTemplate, deleteTemplate } =
    useTemplates(accountId);
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editCategory, setEditCategory] = useState('');

  const openCreate = useCallback(() => {
    setEditId(null);
    setEditName('');
    setEditSubject('');
    setEditBody('');
    setEditCategory('');
    setShowDialog(true);
  }, []);

  const openEdit = useCallback(
    (id: string) => {
      const t = templates.find((x) => x.id === id);
      if (!t) return;
      setEditId(id);
      setEditName(t.name);
      setEditSubject(t.subject || '');
      setEditBody(t.bodyHtml);
      setEditCategory(t.category || '');
      setShowDialog(true);
    },
    [templates],
  );

  const handleSave = useCallback(async () => {
    if (!accountId) return;
    try {
      if (editId) {
        await updateTemplate(editId, {
          name: editName,
          subject: editSubject || undefined,
          bodyHtml: editBody,
          category: editCategory || undefined,
        });
      } else {
        await createTemplate({
          accountId,
          name: editName,
          subject: editSubject || undefined,
          bodyHtml: editBody,
          category: editCategory || undefined,
        });
      }
      setShowDialog(false);
      toast({ title: editId ? 'Template updated' : 'Template created' });
    } catch {
      toast({ title: 'Failed to save template', variant: 'destructive' });
    }
  }, [
    accountId,
    editId,
    editName,
    editSubject,
    editBody,
    editCategory,
    createTemplate,
    updateTemplate,
    toast,
  ]);

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4 max-w-xl">
      <Button size="sm" onClick={openCreate} className="gap-1.5">
        <Plus className="h-4 w-4" /> Create template
      </Button>

      <div className="grid gap-3">
        {templates.map((t) => (
          <Card key={t.id}>
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm">{t.name}</CardTitle>
                  {t.category && (
                    <CardDescription className="text-xs">{t.category}</CardDescription>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => openEdit(t.id)}
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => deleteTemplate(t.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              {t.subject && <p className="text-xs font-medium mb-1">Subject: {t.subject}</p>}
              <div
                className="text-xs text-muted-foreground line-clamp-2"
                dangerouslySetInnerHTML={{ __html: t.bodyHtml }}
              />
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Template' : 'Create Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Template name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <Input
              placeholder="Category (optional)"
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
            />
            <Input
              placeholder="Subject (optional)"
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
            />
            <textarea
              className="w-full min-h-[120px] p-2 text-sm border border-border rounded-md bg-background"
              placeholder="Template body HTML..."
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!editName.trim() || !editBody.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// SNIPPETS TAB
// ============================================================================

function SnippetsTab({ accountId }: { accountId: string | undefined }) {
  const { snippets, loading, createSnippet, updateSnippet, deleteSnippet } = useSnippets(accountId);
  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editTrigger, setEditTrigger] = useState('');
  const [editBody, setEditBody] = useState('');

  const openCreate = useCallback(() => {
    setEditId(null);
    setEditTrigger('/');
    setEditBody('');
    setShowDialog(true);
  }, []);

  const openEdit = useCallback(
    (id: string) => {
      const s = snippets.find((x) => x.id === id);
      if (!s) return;
      setEditId(id);
      setEditTrigger(s.trigger);
      setEditBody(s.body);
      setShowDialog(true);
    },
    [snippets],
  );

  const handleSave = useCallback(async () => {
    if (!accountId) return;
    if (!editTrigger.startsWith('/')) {
      toast({ title: 'Trigger must start with /', variant: 'destructive' });
      return;
    }
    try {
      if (editId) {
        await updateSnippet(editId, { trigger: editTrigger, body: editBody });
      } else {
        await createSnippet({ accountId, trigger: editTrigger, body: editBody });
      }
      setShowDialog(false);
      toast({ title: editId ? 'Snippet updated' : 'Snippet created' });
    } catch {
      toast({ title: 'Failed to save snippet', variant: 'destructive' });
    }
  }, [accountId, editId, editTrigger, editBody, createSnippet, updateSnippet, toast]);

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4 max-w-xl">
      <div>
        <Button size="sm" onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> Create snippet
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          Type the trigger text while composing to expand the snippet.
        </p>
      </div>

      {snippets.map((s) => (
        <Card key={s.id}>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <code className="bg-muted px-1.5 py-0.5 rounded text-xs">{s.trigger}</code>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => openEdit(s.id)}
                >
                  <Edit className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => deleteSnippet(s.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <p className="text-xs text-muted-foreground line-clamp-2">{s.body}</p>
          </CardContent>
        </Card>
      ))}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Snippet' : 'Create Snippet'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Trigger</Label>
              <Input
                placeholder="/trigger"
                value={editTrigger}
                onChange={(e) => setEditTrigger(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm">Body</Label>
              <textarea
                className="w-full min-h-[100px] p-2 text-sm border border-border rounded-md bg-background"
                placeholder="Expansion text..."
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!editTrigger.startsWith('/') || !editBody.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// MODULES TAB
// ============================================================================

const MODULE_INFO: Record<string, { name: string; description: string }> = {
  screener: {
    name: 'Screener',
    description: 'Screen first-time senders before they reach your inbox',
  },
  smart_inbox: {
    name: 'Smart Inbox',
    description:
      'AI-powered categorization of messages into Primary, Feed, Paper Trail, Notifications',
  },
  ai_compose: {
    name: 'AI Compose',
    description: 'AI-assisted email drafting and reply suggestions',
  },
  ai_intelligence: {
    name: 'AI Intelligence',
    description: 'Thread summarization, tone analysis, and phishing detection',
  },
  scheduling: { name: 'Scheduling', description: 'Schedule send, snooze messages, and undo send' },
  templates_snippets: {
    name: 'Templates & Snippets',
    description: 'Reusable email templates and quick-expand text snippets',
  },
  security: {
    name: 'Security',
    description: 'Enhanced phishing detection and sender verification',
  },
  notifications: { name: 'Notifications', description: 'Desktop and mobile push notifications' },
};

function ModulesTab({ accountId }: { accountId: string | undefined }) {
  const { modules, loading, toggleModule } = useEmailModules(accountId);
  const { toast } = useToast();

  const handleToggle = useCallback(
    async (moduleName: string, enabled: boolean) => {
      try {
        await toggleModule(moduleName, enabled);
        toast({
          title: `${MODULE_INFO[moduleName]?.name || moduleName} ${enabled ? 'enabled' : 'disabled'}`,
        });
      } catch {
        toast({ title: 'Failed to toggle module', variant: 'destructive' });
      }
    },
    [toggleModule, toast],
  );

  if (loading) return <Skeleton className="h-32 w-full" />;

  const allModuleNames = Object.keys(MODULE_INFO);

  return (
    <div className="grid gap-3 max-w-xl">
      {allModuleNames.map((name) => {
        const info = MODULE_INFO[name];
        const mod = modules.find((m) => m.moduleName === name);
        const enabled = mod?.enabled ?? false;

        return (
          <Card key={name}>
            <CardHeader className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm">{info.name}</CardTitle>
                  <CardDescription className="text-xs mt-0.5">{info.description}</CardDescription>
                </div>
                <button
                  onClick={() => handleToggle(name, !enabled)}
                  className="flex-shrink-0"
                  aria-label={`Toggle ${info.name}`}
                >
                  {enabled ? (
                    <ToggleRight className="h-6 w-6 text-primary" />
                  ) : (
                    <ToggleLeft className="h-6 w-6 text-muted-foreground" />
                  )}
                </button>
              </div>
            </CardHeader>
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================================
// NOTIFICATIONS TAB
// ============================================================================

function NotificationsTab({ accountId }: { accountId: string | undefined }) {
  const { settings, loading, updateSettings } = useEmailSettings(accountId);
  const { toast } = useToast();

  const save = useCallback(
    async (field: string, value: unknown) => {
      try {
        await updateSettings({ [field]: value });
        toast({ title: 'Settings saved' });
      } catch {
        toast({ title: 'Failed to save', variant: 'destructive' });
      }
    },
    [updateSettings, toast],
  );

  if (loading || !settings) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-6 max-w-xl">
      <div className="space-y-2">
        <Label className="text-sm font-medium">Notification mode</Label>
        <div className="flex gap-3">
          {(['all', 'important', 'none'] as const).map((v) => (
            <Button
              key={v}
              variant={settings.notificationMode === v ? 'default' : 'outline'}
              size="sm"
              onClick={() => save('notificationMode', v)}
            >
              {v === 'all' ? 'All messages' : v === 'important' ? 'Important only' : 'None'}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium">Digest frequency</Label>
        <Select value={settings.digestFrequency} onValueChange={(v) => save('digestFrequency', v)}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="realtime">Real-time</SelectItem>
            <SelectItem value="hourly">Hourly</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="off">Off</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN SETTINGS PAGE
// ============================================================================

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

export function SettingsDialog({ open, onClose }: SettingsDialogProps) {
  const { activeAccount, loading: accountLoading } = useEmailAccounts();
  const accountId = activeAccount?.id;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-0 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Settings className="h-5 w-5" /> Email Settings
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {!accountLoading && !accountId && (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg mb-4">
              No email account found. Use the Accounts tab to create one.
            </div>
          )}

          <Tabs defaultValue="accounts" className="space-y-4">
            <TabsList className="flex flex-wrap h-auto gap-1">
              <TabsTrigger value="accounts" className="gap-1">
                <UserCircle className="h-3.5 w-3.5" /> Accounts
              </TabsTrigger>
              <TabsTrigger value="general" className="gap-1">
                <Settings className="h-3.5 w-3.5" /> General
              </TabsTrigger>
              <TabsTrigger value="signatures" className="gap-1">
                <Pen className="h-3.5 w-3.5" /> Signatures
              </TabsTrigger>
              <TabsTrigger value="filters" className="gap-1">
                <Filter className="h-3.5 w-3.5" /> Filters
              </TabsTrigger>
              <TabsTrigger value="templates" className="gap-1">
                <FileText className="h-3.5 w-3.5" /> Templates
              </TabsTrigger>
              <TabsTrigger value="snippets" className="gap-1">
                <Zap className="h-3.5 w-3.5" /> Snippets
              </TabsTrigger>
              <TabsTrigger value="modules" className="gap-1">
                <Puzzle className="h-3.5 w-3.5" /> Modules
              </TabsTrigger>
              <TabsTrigger value="notifications" className="gap-1">
                <Bell className="h-3.5 w-3.5" /> Notifications
              </TabsTrigger>
            </TabsList>

            <TabsContent value="accounts">
              <AccountsTab />
            </TabsContent>
            <TabsContent value="general">
              <GeneralTab accountId={accountId} />
            </TabsContent>
            <TabsContent value="signatures">
              <SignaturesTab accountId={accountId} />
            </TabsContent>
            <TabsContent value="filters">
              <FiltersTab accountId={accountId} />
            </TabsContent>
            <TabsContent value="templates">
              <TemplatesTab accountId={accountId} />
            </TabsContent>
            <TabsContent value="snippets">
              <SnippetsTab accountId={accountId} />
            </TabsContent>
            <TabsContent value="modules">
              <ModulesTab accountId={accountId} />
            </TabsContent>
            <TabsContent value="notifications">
              <NotificationsTab accountId={accountId} />
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Wrapper for route-based rendering — opens as dialog over inbox */
export function SettingsPage() {
  const navigate = useNavigate();
  return (
    <>
      <InboxPage />
      <SettingsDialog open={true} onClose={() => navigate('/a/arche_email')} />
    </>
  );
}
